/**
 * 3F · REVENDA (COMPRA E VENDA COM ÁGIO) — as contas da venda ao comprador
 * final, sem tela e sem armazenamento, para dar para testar (testes/revenda.mjs).
 *
 * Decisão do gestor (06/10/2026): a porteira de cliente tem dois jeitos.
 *  - Cliente (intermediação): o cliente paga o vendedor direto; o gestor só
 *    recebe a comissão (3B). Nada aqui.
 *  - Revenda: o gestor paga o vendedor e vende ao comprador final, com ágio.
 *    O gado nem desce do caminhão, por isso não passa pela Catira.
 *
 * SEM REDIGITAR PESOS: a venda usa os MESMOS registros da porteira (mesmos
 * animais, mesmos pesos). Muda só o preço: cada lote da compra ganha, na
 * tabela de venda, a base, o preço, o rendimento e os descontos combinados
 * com o comprador final. A conta é a MESMA do romaneio (`calcular`, contas.ts),
 * com os lotes de venda no lugar dos de compra — o romaneio de venda se refaz
 * à mão como o de compra.
 *
 *   venda (líquido)  = bruto da venda − financeiro − funrural da venda
 *   ágio             = venda − compra (valor ao vendedor da porteira)
 *                      − frete e gastos da porteira − comissão a pagar ao comprador
 *
 * A MARGEM FICA PRESERVADA: o vendedor nunca vê o preço de venda (o romaneio
 * de compra não muda); o comprador final nunca vê o preço de compra (o
 * romaneio de venda só tem os lotes de venda); nenhum papel mostra o ágio; o
 * comprador (funcionário) não recebe a tabela de venda nem no aparelho.
 *
 * PORTEIRA ANTIGA («Revenda direta» da 3B, com um valor total digitado
 * dentro da ficha): continua legível — o valor vira uma venda «por valor
 * total», até o gestor pôr preço por lote.
 */
import { calcular, centavos, type RegraDaArroba, type Resultado } from './contas'
import type { Ficha, Lote, LoteDeVenda, Porteira, Recebimento, Registro, VendaDaPorteira } from './tipos'

/** Lote de venda novo: a base e o rendimento do lote da compra; os descontos do lote de venda anterior (3D). */
export function loteDeVendaPadrao(l: Pick<Lote, 'id' | 'base' | 'rendimento'>, anterior?: Pick<LoteDeVenda, 'financeiroPct' | 'funruralPct'> | null): LoteDeVenda {
  return {
    loteId: l.id, base: l.base, preco: null, rendimento: l.rendimento || 50,
    financeiroPct: anterior?.financeiroPct ?? 0, funruralPct: anterior?.funruralPct ?? 0,
  }
}

/**
 * Os lotes da venda no formato de lote do romaneio: o id e a categoria do
 * lote da compra (é o mesmo gado), a base, o preço, o rendimento e os
 * descontos da venda. Lote da compra sem linha na tabela de venda (ex.: o
 * «+ Novo lote» criado no curral depois) entra sem preço — o papel avisa.
 */
export function lotesParaVender(lotesDaCompra: Lote[], venda: Pick<VendaDaPorteira, 'lotes'> | null): Lote[] {
  return lotesDaCompra.map((l) => {
    const v = venda?.lotes.find((x) => x.loteId === l.id)
    return v
      ? { id: l.id, categoria: l.categoria, base: v.base, preco: v.preco !== null && v.preco > 0 ? v.preco : null, rendimento: v.rendimento,
          financeiroPct: v.financeiroPct, funruralPct: v.funruralPct }
      : { id: l.id, categoria: l.categoria, base: l.base, preco: null, rendimento: l.rendimento, financeiroPct: 0, funruralPct: 0 }
  })
}

export interface ContaDaVenda {
  /** lotes: preço por lote (3F) · total: o valor total da «Revenda direta» antiga (3B) */
  modo: 'lotes' | 'total'
  /** a conta lote a lote (modo «lotes»): é o que o romaneio de venda mostra */
  resultado: Resultado | null
  bruto: number
  financeiro: number
  funrural: number
  /** o que o comprador final paga ao gestor (líquido da venda). Nulo: ainda sem preço nenhum. */
  valor: number | null
  /** lotes desta porteira ainda sem preço de venda (fora do valor) */
  semPreco: string[]
}

/**
 * A venda de uma porteira Revenda: os registros DELA (já separados pela
 * apartação), aos preços da tabela de venda. Sem tabela, a porteira antiga
 * com valor total ainda vale («por valor total»). Nem uma nem outra: `null`.
 */
export function contaDaVenda(
  // R1C: a venda segue a regra da arroba da ficha (ficha nova: arroba em 2 casas, como no papel de venda)
  f: Pick<Ficha, 'lotes'> & RegraDaArroba,
  porteira: Pick<Porteira, 'tipo' | 'valorVenda'>,
  registros: Registro[],
  venda: (Pick<VendaDaPorteira, 'lotes'> & Partial<Pick<VendaDaPorteira, 'valorTotal'>>) | null | undefined,
): ContaDaVenda | null {
  if (porteira.tipo !== 'revenda') return null
  if (venda && venda.lotes.length) {
    const r = calcular({ lotes: lotesParaVender(f.lotes, venda), registros, financeiroPct: 0, funruralPct: 0, arrobaEmDuasCasas: f.arrobaEmDuasCasas })
    // lote com animais e sem preço de venda: fora do valor, e a tela avisa
    const semPreco = r.linhas.filter((l) => l.cab > 0 && l.valor === null).map((l) => l.lote.categoria)
    return {
      modo: 'lotes', resultado: r, bruto: r.bruto, financeiro: r.financeiro, funrural: r.funrural,
      valor: r.temPreco ? r.liquido : null, semPreco,
    }
  }
  // R1A: o valor total antigo mora na tabela de venda desde a 013 (saiu da
  // ficha, que vai ao comprador); o da ficha só vale até a 013 rodar
  const total = typeof venda?.valorTotal === 'number' && venda.valorTotal > 0 ? venda.valorTotal : porteira.valorVenda
  if (typeof total === 'number' && total > 0) {
    const v = centavos(total)
    return { modo: 'total', resultado: null, bruto: v, financeiro: 0, funrural: 0, valor: v, semPreco: [] }
  }
  return null
}

/* ---------- recebimentos da venda ---------- */

/** Os recebimentos de uma porteira, em ordem de data. */
export function recebimentosDa(lista: Recebimento[], fichaId: string, destinoId: string): Recebimento[] {
  return lista.filter((x) => x.fichaId === fichaId && x.destinoId === destinoId)
    .sort((a, b) => (a.recebidoEm + a.lancadoEm).localeCompare(b.recebidoEm + b.lancadoEm))
}

export interface SituacaoDoRecebimento {
  recebido: number
  aReceber: number
  quitado: boolean
}

/** Recebido é o que vale: anulado ou recusado pelo servidor não é dinheiro que entrou. */
export function situacaoDoRecebimento(valor: number | null, recebimentos: Pick<Recebimento, 'valor' | 'anuladoEm' | 'recusado'>[]): SituacaoDoRecebimento {
  const recebido = centavos(recebimentos.filter((x) => !x.anuladoEm && !x.recusado).reduce((s, x) => s + x.valor, 0))
  const aReceber = valor === null ? 0 : centavos(Math.max(0, valor - recebido))
  return { recebido, aReceber, quitado: valor !== null && valor > 0 && aReceber === 0 }
}
